import { EventTimeline, JoinRule, MatrixClient, Room } from 'matrix-js-sdk/src';
import moment from 'moment';

import { adaptInvitationResponse, adaptMeetingResponse } from 'api/meedioBackend/adapters';
import { getInvitationDetails } from 'api/meedioBackend/getInvitationDetails';
import { getMeetingByAlias } from 'api/meedioBackend/getMeetingByAlias';
import { getMeetingByRoomId } from 'api/meedioBackend/getMeetingByRoomId';
import { MeetingResponse } from 'api/meedioBackend/types';
import { NETWORK_GUARD_ERROR_MESSAGE, NetworkErrorGuardFn } from 'hooks/useNetworkErrorGuard';
import { extractAlias } from 'modules/CreateNewRoomModal/CreateNewRoomDataProvider/utils';
import { getRoomMetadata } from 'modules/RoomsListItem/utils';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents, getHomeserverFromClient } from 'utils/matrixUtils';
import { getCurrentOrNextOccurrenceWindow, JOIN_PREROOM_THRESHOLD_MS } from 'utils/occurrenceWindow';
import { isMessengerCallRoom } from 'utils/roomHierarchyUtils';

import { InitialRoomIdentityState, RoomIdentityState } from './types';

export enum IdentityRetrieveStatus {
  INITIAL = 'Initial',
  LOADING = 'Loading',
  SUCCESS = 'Success',
  ERROR = 'Error',
}

export const defaultIdentityState: InitialRoomIdentityState = {
  roomName: '',
  roomDescription: '',
  hasWaitingList: false,
  isStarted: false,
};

/**
 * Attaches the home server to the alias.
 * @param alias - The alias (Our version of it, alias-1234)
 * @param roomId - The room ID to extract the home server from.
 * @returns Formatted alias (#alias-1234:matrix..).
 */
export const attachHomeServerToAlias = (alias: string, roomId: string) => {
  const homeserver = roomId.split(':')[1];
  return `#${alias}:${homeserver}`;
};

export const hasRoomIdInIdentityState = (state: InitialRoomIdentityState): state is RoomIdentityState => !!state.roomId;

export interface HandleRoomIdentityProps {
  matrixClient: MatrixClient;
  onSuccess: (roomIdentityState: Partial<RoomIdentityState>) => void;
  onFail: () => void;
  networkErrorGuard: NetworkErrorGuardFn;
}

const isMatrixRoomId = (target: string): boolean => target.startsWith('!');

const tryMsc3266Fallback = async (
  matrixClient: MatrixClient,
  roomId: string,
  onSuccess: HandleRoomIdentityProps['onSuccess'],
  onFail: HandleRoomIdentityProps['onFail']
): Promise<void> => {
  const accessToken = matrixClient.getAccessToken();
  if (!accessToken) {
    onFail();
    return;
  }
  try {
    const encodedRoomId = encodeURIComponent(roomId);
    const url = `${matrixClient.baseUrl}/_matrix/client/unstable/im.nheko.summary/rooms/${encodedRoomId}/summary`;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (response.ok) {
      const summary: { name?: string; canonical_alias?: string; join_rules?: string } = await response.json();
      onSuccess({
        roomId,
        roomName: summary.name || summary.canonical_alias || roomId,
        hasWaitingList: summary.join_rules === JoinRule.Knock,
        isStarted: true,
      });
      return;
    }
  } catch (summaryError) {
    logger.error('handleRoomIdentity: MSC3266 fallback threw', { roomId, summaryError });
  }
  onFail();
};

const resolveFromLocalRoom = async (
  matrixClient: MatrixClient,
  room: Room,
  onSuccess: HandleRoomIdentityProps['onSuccess']
) => {
  const hasWaitingList = room.getJoinRule() === JoinRule.Knock;
  const { description, scheduledToStartAt } = getRoomMetadata(room);
  const roomAlias = extractAlias(room.getCanonicalAlias() || '');
  const isMessenger = isMessengerCallRoom(room);

  if (isMessenger) {
    const meetingResponse = await getMeetingByRoomId(room.roomId).catch(() => null);
    if (meetingResponse) {
      return resolveFromBackendMeeting(matrixClient, meetingResponse, onSuccess);
    }
    return onSuccess({
      roomId: room.roomId,
      roomAlias,
      hasWaitingList,
      roomName: room.name,
      roomDescription: description || undefined,
      startDate: (scheduledToStartAt as string) ?? undefined,
      isStarted: true,
    });
  }

  const meetingResponse = await getMeetingByRoomId(room.roomId).catch(() => null);
  const adapted = meetingResponse ? adaptMeetingResponse(meetingResponse) : null;

  const roomName = adapted?.roomName || room.name;

  const isStartedComputed = adapted?.startedAt ? true : moment(scheduledToStartAt).isSameOrBefore(moment());
  const startDateComputed = adapted?.startedAt ?? adapted?.scheduledAt ?? (scheduledToStartAt as string) ?? undefined;

  return onSuccess({
    roomId: room.roomId,
    roomAlias,
    hasWaitingList,
    roomName,
    roomDescription: description || undefined,
    startDate: startDateComputed,
    isStarted: isStartedComputed,
    meetingId: adapted?.meetingId,
    message: adapted?.message ?? undefined,
    workspaceMatrixRoomId: meetingResponse?.workspace_matrix_room_id,
    isTeamMeeting: meetingResponse?.is_team_meeting,
  });
};

const resolveFromBackendMeeting = (
  matrixClient: MatrixClient,
  meetingResponse: MeetingResponse,
  onSuccess: HandleRoomIdentityProps['onSuccess']
): void => {
  const adapted = adaptMeetingResponse(meetingResponse);
  const room = matrixClient.getRoom(adapted.roomId);
  // Matrix join rule is authoritative for members; IS flag for guests who don't
  // have the room locally yet (mirrored at create time by buildCreateMeetingPayload).
  const hasWaitingList = room ? room.getJoinRule() === JoinRule.Knock : meetingResponse.waiting_list_enabled;
  const { description } = room ? getRoomMetadata(room) : { description: null };

  const isRecurring = meetingResponse.is_recurring;
  const occurrenceWindow = getCurrentOrNextOccurrenceWindow(meetingResponse);
  const occurrenceStart = occurrenceWindow
    ? new Date(occurrenceWindow.startMs).toISOString()
    : meetingResponse.scheduled_at;
  const occurrenceEnd = occurrenceWindow ? new Date(occurrenceWindow.endMs).toISOString() : meetingResponse.ends_at;

  const endedAt = !isRecurring ? meetingResponse.ended_at ?? undefined : undefined;
  const nextOccurrenceAt = isRecurring && occurrenceWindow ? occurrenceStart ?? undefined : undefined;

  const messengerRoomId = meetingResponse.messenger_room_id;
  const messengerRoom = messengerRoomId ? matrixClient.getRoom(messengerRoomId) : null;
  const existingCallState = messengerRoom
    ?.getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
  const existingCallRoomId = existingCallState?.getContent()?.call_room_id as string | undefined;
  const isLiveByMessengerState = existingCallRoomId === meetingResponse.matrix_room_id;
  const callRoom = matrixClient.getRoom(meetingResponse.matrix_room_id);
  const rtcSession = callRoom ? matrixClient.matrixRTC.getActiveRoomSession(callRoom) : null;
  const hasActiveRtc = !!rtcSession && rtcSession.memberships.length > 0;
  const occurrenceStartMs =
    occurrenceWindow?.startMs ?? (meetingResponse.scheduled_at ? Date.parse(meetingResponse.scheduled_at) : null);
  const occurrenceEndMs =
    occurrenceWindow?.endMs ?? (meetingResponse.ends_at ? Date.parse(meetingResponse.ends_at) : null);
  const startedAtMs = meetingResponse.started_at ? Date.parse(meetingResponse.started_at) : null;
  const isStartedByBackend =
    startedAtMs !== null &&
    occurrenceStartMs !== null &&
    startedAtMs >= occurrenceStartMs - JOIN_PREROOM_THRESHOLD_MS &&
    (occurrenceEndMs === null || startedAtMs < occurrenceEndMs);
  const isStartedComputed = isLiveByMessengerState || hasActiveRtc || isStartedByBackend;
  const startDateComputed = occurrenceStart ?? undefined;
  const endsAtComputed = occurrenceEnd ?? undefined;

  onSuccess({
    hasWaitingList,
    roomId: adapted.roomId,
    // Description sourcing (BUG-006a / T-014): prefer the Matrix room topic
    // when present, otherwise fall back to the backend meeting `message`.
    // The pre-meeting page reads `roomDescription` exclusively, so without
    // this fallback meetings created via the backend (no Matrix topic
    // mirrored at create time) would render an empty description card.
    roomDescription: description || adapted.message || undefined,
    roomName: adapted.roomName || room?.name || '',
    roomAlias: adapted.roomAlias || undefined,
    startDate: startDateComputed,
    isStarted: isStartedComputed,
    meetingId: adapted.meetingId,
    // BUG-073: carry the backend messenger room id so end-detection works before
    // the user joins the call room (recurring next-occurrence advancement).
    messengerRoomId: messengerRoomId ?? undefined,
    message: adapted.message ?? undefined,
    endedAt,
    nextOccurrenceAt,
    scheduledAt: meetingResponse.scheduled_at ?? undefined,
    endsAt: endsAtComputed,
    workspaceMatrixRoomId: meetingResponse.workspace_matrix_room_id,
    isTeamMeeting: meetingResponse.is_team_meeting,
    isRecurring,
    recurrenceRule: meetingResponse.recurrence_rule ?? null,
  });
};

const handleMatrixRoomIdTarget = async (
  target: string,
  { matrixClient, onSuccess, onFail, networkErrorGuard }: HandleRoomIdentityProps
) => {
  let room = matrixClient.getRoom(target);
  const membership = room?.getMyMembership();

  if (room && membership === 'invite') {
    try {
      logger.info('handleRoomIdentity: auto-accepting invite', { target });
      await matrixClient.joinRoom(target);
      room = matrixClient.getRoom(target) ?? room;
    } catch (error) {
      logger.warn('handleRoomIdentity: auto-accept invite failed — falling through to backend', { target, error });
    }
  }

  if (room) {
    try {
      await resolveFromLocalRoom(matrixClient, room, onSuccess);
    } catch (error) {
      logger.error('handleRoomIdentity: local resolution failed', { target, error });
      onFail();
    }
    return;
  }

  try {
    const meetingResponse = await networkErrorGuard(() => getMeetingByRoomId(target));
    if (!meetingResponse) {
      logger.info('handleRoomIdentity: backend has no meeting — trying MSC3266 fallback', { target });
      return tryMsc3266Fallback(matrixClient, target, onSuccess, onFail);
    }
    if (meetingResponse.cancelled_at) {
      logger.info('handleRoomIdentity: meeting is cancelled — blocking entry', {
        target,
        meetingId: meetingResponse.id,
      });
      return onFail();
    }
    resolveFromBackendMeeting(matrixClient, meetingResponse, onSuccess);
  } catch (error) {
    const isNetworkGuardError = error instanceof Error && error.message.includes(NETWORK_GUARD_ERROR_MESSAGE);
    if (isNetworkGuardError) return;
    logger.warn('handleRoomIdentity: backend lookup by room id failed — trying MSC3266 fallback', { target, error });
    return tryMsc3266Fallback(matrixClient, target, onSuccess, onFail);
  }
};

const handleAliasTarget = async (
  target: string,
  { matrixClient, onSuccess, onFail, networkErrorGuard }: HandleRoomIdentityProps
) => {
  try {
    const meetingResponse = await networkErrorGuard(() => getMeetingByAlias(target));
    if (!meetingResponse) {
      // Backend has no record for this alias (e.g. Matrix-only room with a canonical alias).
      // Try resolving via the Matrix homeserver before giving up — mirrors the room-ID path.
      const homeserver = getHomeserverFromClient(matrixClient);
      try {
        const { room_id } = await matrixClient.getRoomIdForAlias(`#${target}:${homeserver}`);
        if (room_id) {
          return handleMatrixRoomIdTarget(room_id, { matrixClient, onSuccess, onFail, networkErrorGuard });
        }
      } catch {
        // Alias not known to the homeserver either — fall through to onFail.
      }
      logger.error('handleRoomIdentity: backend has no meeting for alias', { target });
      return onFail();
    }
    if (meetingResponse.cancelled_at) {
      logger.info('handleRoomIdentity: meeting (alias) is cancelled — blocking entry', {
        target,
        meetingId: meetingResponse.id,
      });
      return onFail();
    }
    resolveFromBackendMeeting(matrixClient, meetingResponse, onSuccess);
  } catch (error) {
    const isNetworkGuardError = error instanceof Error && error.message.includes(NETWORK_GUARD_ERROR_MESSAGE);
    if (isNetworkGuardError) return;
    logger.error('handleRoomIdentity: backend lookup by alias failed', { target, error });
    onFail();
  }
};

export const handleRoomIdentityUsingIdOrAlias = async ({
  matrixClient,
  roomIdOrAlias,
  onSuccess,
  onFail,
  networkErrorGuard,
}: HandleRoomIdentityProps & { roomIdOrAlias: string }) => {
  const props: HandleRoomIdentityProps = { matrixClient, onSuccess, onFail, networkErrorGuard };
  if (isMatrixRoomId(roomIdOrAlias)) {
    return handleMatrixRoomIdTarget(roomIdOrAlias, props);
  }
  return handleAliasTarget(roomIdOrAlias, props);
};

export const handleRoomIdentityUsingToken = ({
  matrixClient,
  inviteToken,
  onSuccess,
  onFail,
}: HandleRoomIdentityProps & { inviteToken: string }) => {
  const userId = matrixClient.getUserId();
  if (!inviteToken || !userId) {
    logger.error('Reached invite token flow without invite token or userId', { inviteToken, userId });
    return onFail();
  }

  getInvitationDetails(inviteToken, userId)
    .then((response) => {
      const adapted = adaptInvitationResponse(response);
      const roomId = adapted.matrixRoomId;

      if (!roomId) {
        logger.error('No matrix_room_id in invitation response', { inviteToken });
        return onFail();
      }

      const room = matrixClient.getRoom(roomId);
      const hasWaitingList = room ? room.getJoinRule() === JoinRule.Knock : false;
      const { description } = room ? getRoomMetadata(room) : { description: null };
      const roomAlias = room ? extractAlias(room.getCanonicalAlias() || '') : undefined;

      onSuccess({
        roomId,
        roomName: room?.name || '',
        roomAlias,
        hasWaitingList,
        roomDescription: description || undefined,
        startDate: adapted.startedAt ?? adapted.scheduledAt ?? undefined,
        isStarted:
          !!adapted.startedAt || (adapted.scheduledAt ? moment(adapted.scheduledAt).isSameOrBefore(moment()) : false),
        meetingId: adapted.meetingId ?? undefined,
        scheduledAt: adapted.scheduledAt ?? undefined,
      });
    })
    .catch((error) => {
      logger.error('Error fetching invitation details:', { inviteToken, error });
      const isNetworkGuardError = error instanceof Error && error.message.includes(NETWORK_GUARD_ERROR_MESSAGE);
      if (!isNetworkGuardError) onFail();
    });
};
